import { useState, useEffect } from "react";
import { X, MessageSquare, Plus, RefreshCcw, ListOrdered, Eye } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Card, CardContent } from "../../../../components/ui/card";
import { PreguntaAsambleaRecord } from "../../../../api/preguntaAsambleaService";
import {
  obtenerRespuestasPreguntaAsamblea,
  RespuestaPreguntaAsambleaRecord,
} from "../../../../api/respuestaPreguntaAsambleaService";
import {
  obtenerEstatusRespuestaPreguntaAsamblea,
  EstatusRespuestaPreguntaAsamblea,
} from "../../../../api/estatusRespuestaPreguntaAsambleaService";
import CreateRespuestaPreguntaAsambleaModal from "./CreateRespuestaPreguntaAsambleaModal";
import DetailRespuestaPreguntaAsambleaModal from "./DetailRespuestaPreguntaAsambleaModal";
import Swal from "sweetalert2";

interface RespuestasPreguntaAsambleaModalProps {
  isOpen: boolean;
  pregunta: PreguntaAsambleaRecord | null;
  onClose: () => void;
}

const statusStyles: Record<
  string,
  { text: string; badge: string }
> = {
  A: {
    text: "Activa",
    badge: "text-green-600 bg-green-50 border border-green-200",
  },
  E: {
    text: "Eliminada",
    badge: "text-red-600 bg-red-50 border border-red-200",
  },
};

const getStatusStyle = (estatus: string) => {
  const key = (estatus || "").trim().charAt(0).toUpperCase();
  return (
    statusStyles[key] || {
      text: estatus || "Sin estado",
      badge: "text-gray-600 bg-gray-50 border border-gray-200",
    }
  );
};

export default function RespuestasPreguntaAsambleaModal({
  isOpen,
  pregunta,
  onClose,
}: RespuestasPreguntaAsambleaModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [respuestas, setRespuestas] = useState<RespuestaPreguntaAsambleaRecord[]>([]);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [selectedEstatus, setSelectedEstatus] = useState<string>("A");
  const [estatusList, setEstatusList] = useState<EstatusRespuestaPreguntaAsamblea[]>([]);
  const [isLoadingEstatus, setIsLoadingEstatus] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedRespuesta, setSelectedRespuesta] = useState<RespuestaPreguntaAsambleaRecord | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  useEffect(() => {
    if (isOpen && pregunta) {
      loadEstatus();
      fetchRespuestas();
    } else {
      setRespuestas([]);
      setSelectedEstatus("A");
    }
  }, [isOpen, pregunta]);

  const loadEstatus = async () => {
    try {
      setIsLoadingEstatus(true);
      const response = await obtenerEstatusRespuestaPreguntaAsamblea();
      if (response.code === "1" && response.object_) {
        setEstatusList(response.object_);
      }
    } catch (error) {
      console.error("Error al cargar estatus:", error);
    } finally {
      setIsLoadingEstatus(false);
    }
  };

  const fetchRespuestas = async () => {
    if (!pregunta?.pra_codigo) return;

    setIsLoading(true);
    try {
      const response = await obtenerRespuestasPreguntaAsamblea({
        pregunta_codigo: String(pregunta.pra_codigo),
        codigo: "",
        estatus_codigo: selectedEstatus,
      });

      if (response.code === "1" && Array.isArray(response.object_)) {
        setRespuestas(response.object_);
      } else {
        setRespuestas([]);
      }
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Error al cargar respuestas:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar las respuestas",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && pregunta && selectedEstatus) {
      fetchRespuestas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEstatus]);

  const handleCreateRespuesta = () => {
    setIsCreateModalOpen(true);
  };

  const handleCloseCreateModal = (created: boolean) => {
    setIsCreateModalOpen(false);
    if (created) {
      fetchRespuestas();
    }
  };

  const handleRespuestaClick = (respuesta: RespuestaPreguntaAsambleaRecord) => {
    setSelectedRespuesta(respuesta);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetailModal = (shouldReload?: boolean) => {
    setIsDetailModalOpen(false);
    setSelectedRespuesta(null);
    if (shouldReload) {
      fetchRespuestas();
    }
  };

  if (!isOpen || !pregunta) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="bg-white border-b px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-military-600 flex items-center gap-2">
              <MessageSquare size={28} />
              Respuestas de la Pregunta
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Pregunta: {pregunta.pra_texto}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filtrar por Estatus
              </label>
              {isLoadingEstatus ? (
                <div className="flex items-center gap-2 text-gray-500">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-t-military-500 border-gray-300" />
                  <span className="text-sm">Cargando estatus...</span>
                </div>
              ) : (
                <div className="flex gap-2">
                  {estatusList.map((estatus) => (
                    <label
                      key={estatus.erp_codigo}
                      className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="estatus_respuesta"
                        value={estatus.erp_codigo}
                        checked={selectedEstatus === estatus.erp_codigo}
                        onChange={(e) => setSelectedEstatus(e.target.value)}
                        className="text-military-500 focus:ring-military-500"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        {estatus.erp_nombre}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                className="gap-2 bg-military-500 hover:bg-military-600 text-white"
                onClick={handleCreateRespuesta}
              >
                <Plus size={18} />
                Nueva Respuesta
              </Button>
              <Button
                variant="outline"
                className="gap-2"
                onClick={fetchRespuestas}
                disabled={isLoading}
              >
                <RefreshCcw
                  size={18}
                  className={isLoading ? "animate-spin" : ""}
                />
                Actualizar
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-t-military-500 border-gray-300 mb-4" />
              <p className="text-gray-600">Cargando respuestas...</p>
            </div>
          ) : respuestas.length === 0 ? (
            <div className="text-center py-16">
              <MessageSquare
                size={48}
                className="mx-auto text-gray-400 mb-4"
              />
              <p className="text-gray-600 font-medium">
                No se encontraron respuestas
              </p>
              <p className="text-sm text-gray-500 mt-2">
                {selectedEstatus === "A"
                  ? "Crea una nueva respuesta para esta pregunta."
                  : "No hay respuestas con este estatus."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {respuestas
                .sort((a, b) => {
                  const ordenA = parseInt(a.rpa_orden) || 0;
                  const ordenB = parseInt(b.rpa_orden) || 0;
                  return ordenA - ordenB;
                })
                .map((respuesta) => {
                  const style = getStatusStyle(respuesta.rpa_estatus);
                  return (
                    <Card key={respuesta.rpa_codigo} className="shadow-sm hover:shadow-md transition">
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-sm font-semibold text-gray-500">
                                #{respuesta.rpa_codigo}
                              </span>
                              <span className="text-xs text-gray-400 flex items-center gap-1">
                                <ListOrdered size={12} />
                                Orden: {respuesta.rpa_orden}
                              </span>
                              <span
                                className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${style.badge}`}
                              >
                                {respuesta.rpa_estatus_nombre || style.text}
                              </span>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                              {respuesta.rpa_texto}
                            </h3>
                            {respuesta.erp_total !== undefined && respuesta.erp_total > 0 && (
                              <div className="text-xs text-gray-500">
                                Total: {respuesta.erp_total}
                              </div>
                            )}
                          </div>
                          <Button
                            onClick={() => handleRespuestaClick(respuesta)}
                            variant="outline"
                            className="border-military-500 text-military-600 hover:bg-military-50"
                          >
                            <Eye size={16} className="mr-2" />
                            Ver Detalle
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
            </div>
          )}
        </div>

        <CreateRespuestaPreguntaAsambleaModal
          isOpen={isCreateModalOpen}
          pregunta={pregunta}
          onClose={handleCloseCreateModal}
        />

        <DetailRespuestaPreguntaAsambleaModal
          isOpen={isDetailModalOpen}
          respuesta={selectedRespuesta}
          pregunta={pregunta}
          onClose={handleCloseDetailModal}
        />
      </div>
    </div>
  );
}
